import { useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { ArrowRight, Building2, ChevronDown, ChevronRight, Layers, Network } from 'lucide-react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { departmentsPath, organizationPath, unitsPath } from '@/shared/lib/routes'
import OrganizationTabs from './OrganizationTabs'

interface UnitNode {
  id: number
  name: string
  licenseNumber: string | null
}

interface DepartmentNode {
  id: number
  name: string
  units: UnitNode[]
}

interface SiteNode {
  id: number
  name: string
  country: string | null
  departments: DepartmentNode[]
  unassignedUnits: UnitNode[]
}

interface Props {
  sites: SiteNode[]
  [key: string]: unknown
}

const Hierarchy = () => {
  const { sites } = usePage<Props>().props

  useBreadcrumbs([
    { label: t('nav.administration'), href: organizationPath() },
    { label: t('nav.organization') },
  ])

  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set())
  const [expandedDepts, setExpandedDepts] = useState<Set<number>>(new Set())

  const toggle = (set: Set<number>, id: number): Set<number> => {
    const next = new Set(set)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    return next
  }

  const toggleSite = (id: number) => setExpandedSites((prev) => toggle(prev, id))
  const toggleDept = (id: number) => setExpandedDepts((prev) => toggle(prev, id))

  return (
    <div className="space-y-6">
      <OrganizationTabs />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('organization.hierarchy.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('organization.hierarchy.subtitle')}
          </p>
        </div>
      </div>

      {/* Mobile: hierarchical tree with vertical line */}
      <div className="space-y-4 lg:hidden">
        {sites.map((site) => {
          const isSiteExpanded = expandedSites.has(site.id)
          return (
            <div
              key={site.id}
              className="rounded-lg border bg-card shadow-sm transition-colors hover:bg-muted/30"
            >
              {/* Site header */}
              <div
                className="flex items-center gap-2 px-4 py-4 cursor-pointer"
                onClick={() => toggleSite(site.id)}
                role="button"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground">
                  {isSiteExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
                <Building2
                  className="h-5 w-5 shrink-0 text-muted-foreground"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <span className="flex-1 min-w-0 font-semibold">{site.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">(ID: {site.id})</span>
                {site.departments.length > 0 && (
                  <Link
                    href={`${departmentsPath()}?site_id=${site.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                  >
                    {t('organization.hierarchy.departmentsSummary', {
                      count: site.departments.length,
                    })}
                    <ArrowRight className="size-3 shrink-0" />
                  </Link>
                )}
              </div>

              {/* Departments with vertical hierarchy line */}
              {isSiteExpanded && site.departments.length > 0 && (
                <div className="border-t px-4 py-3">
                  <div className="ml-7 border-l-2 border-border pl-4 space-y-4">
                    {site.departments.map((dept) => {
                      const isDeptExpanded = expandedDepts.has(dept.id)
                      return (
                        <div key={dept.id}>
                          {/* Dept row */}
                          <div className="flex items-start gap-2">
                            <button
                              type="button"
                              onClick={() => dept.units.length > 0 && toggleDept(dept.id)}
                              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground ${dept.units.length > 0 ? 'hover:bg-muted' : 'cursor-default'}`}
                            >
                              {dept.units.length > 0 &&
                                (isDeptExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                ))}
                            </button>
                            <Network
                              className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                              strokeWidth={1.75}
                              aria-hidden
                            />
                            <div className="flex-1 min-w-0 space-y-1.5">
                              <div className="flex items-baseline gap-2">
                                <p className="text-sm font-medium">{dept.name}</p>
                                <span className="text-xs text-muted-foreground">
                                  (ID: {dept.id})
                                </span>
                              </div>
                              {dept.units.length > 0 && (
                                <Link
                                  href={`${unitsPath()}?department_id=${dept.id}`}
                                  className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                                >
                                  {t('organization.hierarchy.unitsSummary', {
                                    count: dept.units.length,
                                  })}
                                  <ArrowRight className="size-3 shrink-0" />
                                </Link>
                              )}
                            </div>
                          </div>

                          {/* Units */}
                          {isDeptExpanded && dept.units.length > 0 && (
                            <div className="ml-7 mt-2 border-l-2 border-border/50 pl-4 space-y-2">
                              {dept.units.map((unit) => (
                                <div key={unit.id} className="flex items-center gap-2">
                                  <Layers
                                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
                                    strokeWidth={1.75}
                                    aria-hidden
                                  />
                                  <span className="text-sm text-muted-foreground">{unit.name}</span>
                                  {unit.licenseNumber && (
                                    <span className="text-xs text-muted-foreground/50">
                                      {t('organization.hierarchy.licenseNumber')}:{' '}
                                      {unit.licenseNumber}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Desktop: site-level collapsible tree */}
      <div className="hidden space-y-3 lg:block">
        {sites.map((site) => {
          const isExpanded = expandedSites.has(site.id)
          return (
            <div
              key={site.id}
              className="rounded-lg border bg-card shadow-sm transition-colors hover:bg-muted/30"
            >
              {/* Site header row */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                onClick={() => toggleSite(site.id)}
                role="button"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
                <Building2
                  className="h-5 w-5 shrink-0 text-muted-foreground"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <span className="flex-1 font-semibold">{site.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">(ID: {site.id})</span>
                {site.departments.length > 0 && (
                  <Link
                    href={`${departmentsPath()}?site_id=${site.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                  >
                    {t('organization.hierarchy.departmentsSummary', {
                      count: site.departments.length,
                    })}
                    <ArrowRight className="size-3 shrink-0" />
                  </Link>
                )}
              </div>

              {/* Departments */}
              {isExpanded && site.departments.length > 0 && (
                <div className="border-t">
                  {site.departments.map((dept) => {
                    const isDeptExpanded = expandedDepts.has(dept.id)
                    return (
                      <div
                        key={dept.id}
                        className={`border-b last:border-b-0 transition-colors ${dept.units.length > 0 ? 'hover:bg-muted/30' : ''}`}
                      >
                        {/* Department row */}
                        <div
                          className={`flex items-center gap-2 px-4 py-3 ${dept.units.length > 0 ? 'cursor-pointer' : ''}`}
                          onClick={() => dept.units.length > 0 && toggleDept(dept.id)}
                          role={dept.units.length > 0 ? 'button' : undefined}
                        >
                          {/* Indent: aligns under site chevron */}
                          <div className="w-6 shrink-0" aria-hidden />
                          {/* Dept collapse chevron */}
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground">
                            {dept.units.length > 0 &&
                              (isDeptExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              ))}
                          </span>
                          <Network
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            strokeWidth={1.75}
                            aria-hidden
                          />
                          {/* Left group: name */}
                          <div className="flex flex-1 min-w-0 items-center gap-2">
                            <span className="text-sm">{dept.name}</span>
                            <span className="text-xs font-normal text-muted-foreground">
                              (ID: {dept.id})
                            </span>
                          </div>
                          {/* Units pill */}
                          {dept.units.length > 0 && (
                            <Link
                              href={`${unitsPath()}?department_id=${dept.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                            >
                              {t('organization.hierarchy.unitsSummary', {
                                count: dept.units.length,
                              })}
                              <ArrowRight className="size-3 shrink-0" />
                            </Link>
                          )}
                        </div>

                        {/* Units list */}
                        {isDeptExpanded && dept.units.length > 0 && (
                          <div className="border-t bg-muted/30">
                            {dept.units.map((unit) => (
                              <div
                                key={unit.id}
                                className="flex items-center gap-2 border-b px-4 py-2 pl-24 last:border-b-0"
                              >
                                <Layers
                                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
                                  strokeWidth={1.75}
                                  aria-hidden
                                />
                                <span className="text-sm text-muted-foreground">{unit.name}</span>
                                {unit.licenseNumber && (
                                  <span className="text-xs text-muted-foreground/60">
                                    {t('organization.hierarchy.licenseNumber')}:{' '}
                                    {unit.licenseNumber}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {isExpanded && site.departments.length === 0 && (
                <div className="border-t px-14 py-3">
                  <p className="text-sm text-muted-foreground">
                    {t('organization.hierarchy.noDepartments')}
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Hierarchy
